// Text-to-speech and speech recognition: the browser's Web Speech API on the web,
// native plugins inside the app (Android WebView has no Web Speech API).
import { Capacitor } from '@capacitor/core';
import { TextToSpeech } from '@capacitor-community/text-to-speech';
import { SpeechRecognition as NativeRecognition } from '@capacitor-community/speech-recognition';

const native = Capacitor.isNativePlatform();

export function stopSpeaking() {
  if (native) TextToSpeech.stop().catch(() => {});
  else if (window.speechSynthesis) window.speechSynthesis.cancel();
}

// Resolves when the utterance finishes (or fails)
export function speak(text, rate = 0.9) {
  if (native) {
    // The plugin's rate 1.0 is the platform's normal speed, same as the Web Speech API
    return TextToSpeech.stop().catch(() => {})
      .then(() => TextToSpeech.speak({ text, lang: 'en-US', rate }))
      .catch(() => {});
  }
  if (!window.speechSynthesis) return Promise.resolve();
  window.speechSynthesis.cancel();
  return new Promise((resolve) => {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'en-US';
    u.rate = rate;
    u.onend = u.onerror = () => resolve();
    window.speechSynthesis.speak(u);
  });
}

// Plays lines one after another; isActive() lets the caller stop between lines
export async function speakAll(lines, isActive = () => true) {
  for (const t of lines) {
    if (!isActive()) return;
    await speak(t);
  }
}

const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
// The plugin is only the fallback (Android WebView); browsers and iOS WebKit use the Web Speech API
const useNativeRecognition = native && !Recognition;
export const canListen = useNativeRecognition || !!Recognition;

// Resolves with what the user said (best guesses), rejects on error/no speech
export async function listenOnce() {
  if (useNativeRecognition) {
    const { speechRecognition } = await NativeRecognition.requestPermissions();
    if (speechRecognition !== 'granted') throw new Error('not-allowed');
    const timer = setTimeout(() => NativeRecognition.stop().catch(() => {}), 8000);
    try {
      const { matches } = await NativeRecognition.start({ language: 'en-US', maxResults: 3, partialResults: false, popup: false });
      if (!matches || !matches.length) throw new Error('no-speech');
      return matches;
    } catch (e) {
      throw new Error(e.message === 'not-allowed' ? e.message : 'no-speech');
    } finally { clearTimeout(timer); }
  }
  return new Promise((resolve, reject) => {
    if (!Recognition) return reject(new Error('unsupported'));
    const r = new Recognition();
    r.lang = 'en-US';
    r.interimResults = false;
    r.maxAlternatives = 3;
    let done = false;
    r.onresult = (e) => { done = true; resolve([...e.results[0]].map(a => a.transcript)); };
    r.onerror = (e) => { done = true; reject(new Error(e.error)); };
    r.onend = () => { if (!done) reject(new Error('no-speech')); };
    r.start();
  });
}

// Lowercase words without punctuation, for answer comparison
export const words = (s) => s.toLowerCase().replace(/[^a-z0-9' ]/g, ' ').split(/\s+/).filter(Boolean);

// Share of the target's words that the user said (order-insensitive)
export function overlap(target, said) {
  const t = words(target);
  const s = new Set(words(said));
  return t.length ? t.filter(w => s.has(w)).length / t.length : 0;
}

// Short success/failure tone, plus a vibration on failure where supported
export function feedbackTone(ok) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = ok ? 880 : 220;
    g.gain.setValueAtTime(0.08, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.25);
  } catch (e) { /* audio not available */ }
  if (!ok && navigator.vibrate) navigator.vibrate(120);
}
